import React, { useState, useEffect } from "react";
import '../Dashboard.css';
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';


const PersonalStep = ({ onNext, onBack }) => {
  const [formData, setFormData] = useState({
    user_id: "",
    profile_pic: "",
    name: "",
    id_number: "",
    email: "",
    phone_number: "",
    dof: "",
    firm: "",
    postal_address: "",
    certificate_of_registration: false,
  });

  const [imagePreview, setImagePreview] = useState(""); // for image preview

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = `https://portal.lsb.go.ke/account/personal_data`;
      const accessToken = localStorage.getItem("accessToken");

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch personal data.");

        const data = await response.json();

        setFormData((prevState) => ({
          ...prevState,
          user_id: data.user_id,
          id_number: data.id_number,
          name: data.name,
          profile_pic: data.profile_pic,
          email: data.email,
          phone_number: data.phone_number,
          dof: data.dof,
          firm: data.firm,
          postal_address: data.postal_address,
          certificate_of_registration: data.certificate_of_registration || false,
        }));

        // If we have a URL for the pic, set it for preview
        if (data.profile_pic && typeof data.profile_pic === "string") {
          setImagePreview(data.profile_pic);
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    };

    fetchData();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
      setFormData({ ...formData, profile_pic: file });
      setImagePreview(URL.createObjectURL(file)); // show preview immediately
    } else {
      Swal.fire({
        title: "Error",
        text: "Please upload a valid PNG or JPG file.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setImagePreview(""); // Remove preview on invalid file
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleSubmit = async () => {
    const apiUrl = `https://portal.lsb.go.ke/account/personal_data/`;
    const accessToken = localStorage.getItem("accessToken");

    if (!formData.id_number) {
      Swal.fire({
        title: "Error",
        text: "ID Number is required.",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const submissionData = new FormData();
    submissionData.append("personal_data_completed", true);
    submissionData.append("id", formData.user_id);
    submissionData.append("step_1", true);

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "user_id") return;
      if (key === "profile_pic" && value instanceof File) {
        submissionData.append("profile_pic", value);
      } else if (key !== "name") {
        submissionData.append(key, value);
      }
    });

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: submissionData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to submit personal data.");
      }

      Swal.fire({
        title: "Success",
        text: "Personal data saved successfully!",
        icon: "success",
        confirmButtonText: "OK",
      });

      onNext();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        confirmButtonText: "Retry",
      });
    }
  };

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    Swal.fire({
      title: 'Success',
      text: 'You have been logged out.',
      icon: 'success',
      width: 370,
    });
    navigate('/portal');
  };

  return (
    <div className="">
      <div className="">
        <section className="">
          <div className="flex justify-between items-center pb-6">
            <h3 className="font-bold text-[#3d0707] tracking-wide underline underline-offset-4 decoration-[#ebcab3]">
              Personal Data
            </h3>

            <button
              onClick={handleLogout}
              className="py-2 px-3 bg-red-600 hover:font-bold uppercase text-white flex gap-2 items-center rounded-md text-sm transition-transform duration-200 ease-in-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm hover:shadow-lg"
            >
              <span className="transition duration-200 ease-in-out group-hover:pr-1">Logout</span>
              <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:rotate-[-15deg]" />
            </button>
          </div>

          <div className="mb-6 leading-tight">
            <p className="font-medium">Provide personal details and make changes where necessary.</p>
            <p className="font-medium"><b>Note:</b> <b className="text-[#ff0000]">*</b> marks required entries</p>
          </div>
        </section>

        {/* Inputs Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* name */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 block text-xs tracking-widest">
              Full Name
            </label>
            <input
              type="text"
              placeholder="Name"
              value={formData.name || ""}
              className="mt-1 w-full rounded-md border-gray-200 bg-gray-100 text-base font-semibold text-gray-700 shadow focus:outline-sky-400 uppercase cursor-not-allowed"
              readOnly
            />
          </div>

          {/* id_number */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 block text-xs tracking-widest">
              ID Number
            </label>
            <input
              type="number"
              placeholder="ID Number"
              value={formData.id_number || ""}
              className="mt-1 w-full rounded-md border-gray-200 bg-gray-100 text-base font-semibold text-gray-700 shadow cursor-not-allowed"
              readOnly
            />
          </div>

          {/* Email Address */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 text-xs tracking-widest flex gap-1">Email address <p className="text-[#ff0000]">*</p></label>
            <input
              type="email"
              placeholder="Email Address"
              value={formData.email || ""}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="mt-1 w-full rounded-md border-gray-200 bg-white text-base text-gray-700 shadow font-semibold focus:outline-sky-400"
            />
          </div>

          {/* Phone Number */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 text-xs tracking-widest flex gap-1">Phone number <p className="text-[#ff0000]">*</p></label>
            <input
              type="number"
              placeholder="Phone Number"
              value={formData.phone_number || ""}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              className="mt-1 w-full rounded-md border-gray-200 bg-white text-base text-gray-700 shadow font-semibold focus:outline-sky-400"
              required
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 text-xs tracking-widest flex gap-1">Date of birth  <p className="text-[#ff0000]">*</p></label>
            <input
              type="date"
              value={formData.dof || ""}
              onChange={(e) => setFormData({ ...formData, dof: e.target.value })}
              className="mt-1 w-full rounded-md border-gray-200 bg-white text-base text-gray-700 shadow font-semibold focus:outline-sky-400"
              required
            />
          </div>

          {/* Firm */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 text-xs tracking-widest flex gap-1">Firm  <p className="text-[#ff0000]">*</p></label>
            <input
              type="text"
              placeholder="Firm"
              value={formData.firm || ""}
              onChange={(e) => setFormData({ ...formData, firm: e.target.value })}
              className="mt-1 w-full rounded-md border-gray-200 bg-white text-base text-gray-700 shadow font-semibold focus:outline-sky-400"
              required
            />
          </div>

          {/* Postal Address */}
          <div>
            <label className="text-gray-500 uppercase font-bold mb-1 text-xs tracking-widest flex gap-1">Postal address <p className="text-[#ff0000]">*</p></label>

            <input
              placeholder="Postal Address"
              value={formData.postal_address || ""}
              onChange={(e) => setFormData({ ...formData, postal_address: e.target.value })}
              className="mt-1 w-full rounded-md border-gray-200 bg-white text-base text-gray-700 shadow font-semibold focus:outline-sky-400"
              required
            />
          </div>
        </div>

        {/* Certificate of Registration */}
        <div className="flex items-center pt-5">
          <input
            id="certificate_of_registration"
            name="certificate_of_registration"
            type="checkbox"
            checked={formData.certificate_of_registration}
            onChange={handleCheckboxChange}
            className="h-5 w-5 rounded border-gray-300 text-sky-600 focus:ring-sky-500 transition-transform duration-150 mr-2"
          />

          <label htmlFor="certificate_of_registration" className="font-semibold italic text-gray-800 text-sm cursor-pointer">
            I have a Certificate of Registration as a Licensed Surveyor
          </label>
        </div>

        {/* Profile Picture Section at the Bottom */}
        <div className="pt-0 mt-8 border-t border-gray-100">
          <h3 className="text-[#3d0707] text-lg font-semibold mb-3 mt-6 text-center">
            Profile Picture
          </h3>

          <div className="flex flex-col items-center gap-2">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Profile preview"
                className="h-32 w-32 object-cover object-top rounded-full border-2 border-sky-300 mb-2 shadow-lg"
              />
            ) : (
              <div className="h-32 w-32 flex items-center justify-center rounded-full bg-gray-100 border-2 border-gray-200 mb-2 text-gray-400">
                <svg width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto" viewBox="0 0 24 24"><circle cx="12" cy="9" r="3" /><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 19a6 6 0 0113.758 0M12 22V19" /></svg>
              </div>
            )}
            <span
              htmlFor="profile_pic"
              className="block font-semibold text-gray-700"
              style={{ fontSize: ".96em", cursor: "pointer" }}
            >
              Upload Profile Picture
            </span>

            <label className="uppercase text-xs text-red-600 font-semibold mb-2" htmlFor="profile_pic">
              [ *Ensure background is white ]
            </label>

            <input
              id="profile_pic"
              type="file"
              onChange={handleFileChange}
              className="w-full max-w-xs rounded-lg border border-sky-200 bg-white text-gray-700 shadow p-1 file:bg-sky-50 file:rounded file:border-none file:py-2 file:px-4 file:font-semibold"
              accept=".png,.jpg,.jpeg"
              style={{ display: "block" }}
              required
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="mt-8 mb-10 flex flex-col md:flex-row gap-4 items-center">
          <button onClick={onBack} className="button">
            <span className="button_lg">
              <span className="button_sl"></span>
              Back
            </span>
          </button>

          <button
            onClick={handleSubmit}
            className="button"
          >
            <span className="button_lg">
              <span className="button_sl"></span>
              Next
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersonalStep;